
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import type { DonationCampaign, User } from '../../utils/supabase';
import ContactFooter from '../../components/feature/ContactFooter';

export default function DonationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();               
  const [campaign, setCampaign] = useState<DonationCampaign | null>(null);
  const [shelter, setShelter] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Cargar datos de la campaña
  useEffect(() => {
    if (id) {
      loadCampaignData();
    }
  }, [id]);

  const loadCampaignData = async () => {
    try {
      // Cargar campaña
      const { data: campaignData } = await supabase
        .from('donation_campaigns')
        .select('*')
        .eq('id', id)
        .single();

      if (campaignData) {
        setCampaign(campaignData);

        // Cargar datos del refugio
        const { data: userData } = await supabase
          .from('users')
          .select('*')
          .eq('id', campaignData.user_id)
          .single();

        setShelter(userData);
      }
    } catch (error) {
      console.error('Error cargando campaña:', error);
    } finally {
      setLoading(false);
    }
  };

  // Functions from original code
  const handleCall = () => {
    const phoneNumber = campaign?.contact_phone || shelter?.phone;
    if (phoneNumber) {
      window.open(`tel:${phoneNumber}`, '_self');
    } else {
      alert('No hay número de teléfono disponible');
    }
  };

  const handleWhatsApp = () => {
    if (campaign?.whatsapp_link) {
      window.open(campaign.whatsapp_link, '_blank');
    } else if (shelter?.phone) {
      const message = `Hola, me interesa ayudar con la campaña "${campaign?.title}" para ${campaign?.title.split(' ')[0]}`;
      const whatsappUrl = `https://wa.me/${shelter.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } else {
      alert('No hay WhatsApp disponible');
    }
  };

  const handleDonate = () => {
    setShowDonationModal(true);
  };

  const handlePaymentOption = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
    setShowDonationModal(false);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      alert('¡Enlace copiado al portapapeles!');
      setShowShareModal(false);
    }).catch(() => {
      // Si falla, mostrar el enlace en un prompt
      prompt('Copia este enlace:', url);
      setShowShareModal(false);
    });
  };

  const handleViewOtherCases = () => {
    navigate('/', { state: { activeTab: 'donar' } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando campaña...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Campaña no encontrada</h2>
          <p className="text-gray-600 mb-6">La campaña que buscas no existe o ha sido eliminada.</p>
          <button
            onClick={() => navigate('/')}        
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  // Imágenes de galería - Usando imágenes reales de la campaña
  const campaignImages = [
    campaign.image_url,
    campaign.image_url_2,
    campaign.image_url_3,
    campaign.image_url_4
  ].filter(Boolean);

  // Si no hay imágenes, usar imágenes por defecto
  const galleryImages = campaignImages.length > 0 ? campaignImages : [
    'https://readdy.ai/api/search-image?query=Beautiful%20golden%20retriever%20rescue%20dog%20in%20veterinary%20care%2C%20clean%20professional%20medical%20environment%2C%20single%20uniform%20background%2C%20no%20text%20or%20signage%20visible%2C%20compassionate%20animal%20rescue%20photography%2C%20high%20quality%20portrait%20style%20image%20with%20consistent%20lighting%20and%20smooth%20background&width=800&height=600&seq=mainphoto1&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Veterinary%20medical%20treatment%20for%20rescue%20dog%2C%20caring%20hands%20helping%20injured%20pet%2C%20professional%20veterinary%20clinic%20setting%2C%20compassionate%20animal%20care%20with%20clean%20medical%20background&width=800&height=600&seq=gallery2&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Recovery%20progress%20of%20rescued%20dog%2C%20happy%20healthy%20pet%20after%20medical%20treatment%2C%20heartwarming%20transformation%20story%2C%20professional%20pet%20photography%20with%20simple%20background&width=800&height=600&seq=gallery3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Dog%20shelter%20facility%20caring%20environment%2C%20clean%20modern%20animal%20rescue%20center%2C%20dedicated%20volunteers%20working%20with%20rescued%20pets%2C%20professional%20facility%20photography&width=800&height=600&seq=gallery4&orientation=landscape',
  ];

  const progressPercentage = (campaign.raised / campaign.goal) * 100;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/')}          
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Volver</span>
            </button>

            <div className="flex items-center space-x-3">
              <img
                src="/images/image.png"
                alt="Huellitas Unidas Logo"
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-lg font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                Huellitas Unidas
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={handleShare}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-share-line text-xl text-gray-600"></i>
              </button>
              <button className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-heart-line text-xl text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Gallery Section - Updated for multiple images */}
        <div className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-96 lg:h-[500px]">
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={galleryImages[activeImageIndex]}
                alt={campaign.title}
                className="w-full h-full object-cover object-top cursor-pointer"
              />
              {campaign.urgent && (
                <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-semibold">
                  <i className="ri-alarm-warning-fill mr-2"></i>
                  URGENTE
                </div>
              )}
              
              {/* Image counter */}
              {galleryImages.length > 1 && (
                <div className="absolute top-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                  {activeImageIndex + 1} / {galleryImages.length}
                </div>
              )}
              
              {/* Navigation arrows - Only show if multiple images */}
              {galleryImages.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImageIndex(activeImageIndex > 0 ? activeImageIndex - 1 : galleryImages.length - 1)}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-line text-gray-800"></i>
                  </button>
                  <button
                    onClick={() => setActiveImageIndex(activeImageIndex < galleryImages.length - 1 ? activeImageIndex + 1 : 0)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-gray-800"></i>
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Grid - Only show if multiple images */}
            {galleryImages.length > 1 && (
              <div className="hidden lg:grid grid-cols-2 gap-4">
                {galleryImages.slice(1, 5).map((image, index) => (
                  <div
                    key={index + 1}
                    className="relative rounded-2xl overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setActiveImageIndex(index + 1)}
                  >
                    <img src={image} alt={`${campaign.title} ${index + 2}`} className="w-full h-full object-cover object-top" />
                    {index === 3 && galleryImages.length > 4 && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white font-semibold">Ver todas las fotos</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Gallery Dots - Only show if multiple images */}
          {galleryImages.length > 1 && (
            <div className="flex justify-center space-x-2 mt-4 lg:hidden">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImageIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors cursor-pointer ${
                    activeImageIndex === index ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title and Basic Info */}
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{campaign.title}</h1>

              <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
                <div className="flex items-center space-x-2">
                  <i className="ri-map-pin-fill text-red-5"></i>
                  <span>
                    {shelter?.city}, {shelter?.state}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-building-fill text-blue-5"></i>
                  <span>{shelter?.shelter_name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-percent-fill text-green-5"></i>
                  <span>{Math.round(progressPercentage)}% completado</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="bg-gray-100 rounded-2xl p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">
                      ${campaign.raised.toLocaleString()}
                    </div>
                    <div className="text-gray-600">recaudado de ${campaign.goal.toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-500">{Math.round(progressPercentage)}%</div>
                    <div className="text-gray-600">completado</div>
                  </div>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                  <div
                    className="bg-gradient-to-r from-orange-400 to-orange-600 h-4 rounded-full transition-all duration-1000"
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                  ></div>
                </div>

                <div className="text-sm text-gray-600 text-center">
                  {progressPercentage >= 100
                    ? '¡Meta alcanzada!'
                    : `Faltan $${(campaign.goal - campaign.raised).toLocaleString()} para alcanzar la meta`}
                </div>
              </div>
            </div>

            {/* Story Section */}
            <div className="bg-white">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-heart-fill text-red-5 mr-3"></i>
                Historia de {campaign.title.split(' ')[0] || 'este caso'}
              </h2>

              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="mb-6">{campaign.description}</p>
              </div>
            </div>

            {/* Shelter Info */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-building-line text-blue-5 mr-3"></i>
                Información del Refugio
              </h3>

              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {shelter?.shelter_name?.charAt(0) || 'R'}
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{shelter?.shelter_name}</h4>
                  <div className="space-y-2 text-gray-600">
                    <div className="flex items-center space-x-2">
                      <i className="ri-map-pin-fill text-red-5"></i>
                      <span>
                        {shelter?.city}, {shelter?.state}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-phone-fill text-green-5"></i>
                      <span>{shelter?.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-calendar-fill text-blue-5"></i>
                      <span>Miembro desde {new Date(shelter?.created_at || '').getFullYear()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Contact & Donation */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-gray-900 mb-2">
                    ${campaign.raised.toLocaleString()}
                  </div>
                  <div className="text-gray-600">de ${campaign.goal.toLocaleString()}</div>
                </div>

                <div className="space-y-4 mb-8">
                  <button
                    onClick={handleDonate}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-heart-fill mr-2"></i>
                    Donar Ahora
                  </button>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={handleCall}
                      className="flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-fill"></i>
                      <span>Llamar</span>
                    </button>

                    <button
                      onClick={handleWhatsApp}
                      className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-fill"></i>
                      <span>WhatsApp</span>
                    </button>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="text-center text-sm text-gray-600 mb-4">
                    <i className="ri-shield-check-fill text-green-500 mr-1"></i>
                    Donación 100% segura
                  </div>

                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex justify-between items-center text-sm">
                      <span>Progreso:</span>
                      <span className="font-semibold text-green-500">{Math.round(progressPercentage)}% completado</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span>Recaudado:</span>
                      <span className="font-semibold text-orange-500">
                        ${campaign.raised.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-6 bg-gray-50 rounded-2xl p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Otras formas de ayudar</h3>
                <div className="space-y-3">
                  <button 
                    onClick={handleShare}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer"
                  >
                    <i className="ri-share-fill text-blue-5"></i>
                    <span className="text-gray-700">Compartir campaña</span>
                  </button>
                  <button className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer">
                    <i className="ri-hand-heart-fill text-purple-5"></i>
                    <span className="text-gray-700">Ser voluntario</span>
                  </button>
                  <button 
                    onClick={handleViewOtherCases}
                    className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-white transition-colors cursor-pointer"
                  >
                    <i className="ri-group-fill text-green-5"></i>
                    <span className="text-gray-700">Ver otros casos</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-fill text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                ¡Ayuda a {campaign?.title.split(' ')[0] || 'este caso'}!
              </h3>
              <p className="text-gray-600">Elige tu método de donación preferido</p>
            </div>

            <div className="space-y-4 mb-6">
              {/* Información Bancaria */}
              {campaign?.bank_info && (
                <div className="bg-blue-50 rounded-2xl p-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center">
                    <i className="ri-bank-fill mr-2"></i>
                    Transferencia Bancaria
                  </h4>
                  <div className="text-sm text-blue-700 whitespace-pre-line">{campaign.bank_info}</div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(campaign.bank_info || '');
                      alert('Información copiada al portapapeles');
                    }}
                    className="mt-2 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-full transition-colors cursor-pointer"
                  >
                    <i className="ri-file-copy-line mr-1"></i>
                    Copiar información
                  </button>
                </div>
              )}

              {/* Botones de Pago Personalizados */}
              {campaign?.payment_button1_enabled && campaign?.payment_button1_name && campaign?.payment_button1_url && (
                <button
                  onClick={() => handlePaymentOption(campaign.payment_button1_url!)}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-secure-payment-fill mr-2"></i>
                  {campaign.payment_button1_name}
                </button>
              )}

              {campaign?.payment_button2_enabled && campaign?.payment_button2_name && campaign?.payment_button2_url && (
                <button
                  onClick={() => handlePaymentOption(campaign.payment_button2_url!)}
                  className="w-full bg-gradient-to-r from-purple-5  to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center"
                >
                  <i className="ri-secure-payment-fill mr-2"></i>
                  {campaign.payment_button2_name}
                </button>
              )}

              {/* Mensaje si no hay opciones de pago configuradas */}
              {!campaign?.bank_info && !campaign?.payment_button1_enabled && !campaign?.payment_button2_enabled && (
                <div className="text-center py-8">
                  <i className="ri-information-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-500">
                    El refugio aún no ha configurado métodos de donación
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Puedes contactarlos directamente para más información
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <div className="text-center text-sm text-gray-600 mb-4">
                <i className="ri-shield-check-fill text-green-500 mr-1"></i>
                Donación directa al refugio
              </div>

              <button
                onClick={() => setShowDonationModal(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-share-fill text-2xl text-blue-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Compartir Campaña</h3>
              <p className="text-gray-600">Copia el enlace para compartir esta campaña</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enlace de la campaña:
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={window.location.href}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-600 focus:outline-none"
                />
                <button
                  onClick={handleCopyLink}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer flex items-center"
                >
                  <i className="ri-file-copy-line mr-1"></i>
                  Copiar
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowShareModal(false)}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Contact Footer */}
      <ContactFooter />
    </div>
  );
}
